import React from 'react'
import type {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material'
import { Company } from 'models'
import type { SelectionState } from 'contexts/CompaniesContext'
import { SelectionSummary } from '../../types/selectionTypes'
import { useChildSelectionView } from './useChildSelectionView'
import { useChildSelectorBehavior } from '../useChildSelectorBehavior'

const useChildSelectorControl = (params: {
  childrenCompanies: Array<Company>
  pendingSelectionState: SelectionState
  validatedSelectionState: SelectionState
  isAllAccountsSelected: boolean
  isChildSelectorDisabled: boolean
  pendingSelectionLimit: number
  pendingSelectionLimitReached: boolean
  hasPendingSelection: boolean
  allAccountsOption?: Company
  isOrphanSelected: boolean
  childrenSearchValue: string
  canSelectAllChildren: boolean
  isChildrenLoading: boolean
  onSelectionChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Array<Company>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Company>,
  ) => void
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void
  getOptionDisabled: (option: Company) => boolean
}) => {
  const {
    childrenCompanies,
    pendingSelectionState,
    validatedSelectionState,
    isAllAccountsSelected,
    isChildSelectorDisabled,
    pendingSelectionLimit,
    pendingSelectionLimitReached,
    hasPendingSelection,
    allAccountsOption,
    isOrphanSelected,
    childrenSearchValue,
    canSelectAllChildren,
    isChildrenLoading,
    onSelectionChange,
    onInputChange,
    getOptionDisabled,
  } = params

  const selectionView = useChildSelectionView({
    pendingSelectionState,
    pendingSelectionLimit,
    pendingSelectionLimitReached,
    isChildSelectorDisabled,
    allAccountsOption,
    isOrphanSelected,
    childrenSearchValue,
  })

  const dropdownBehavior = useChildSelectorBehavior({
    childrenCompanies,
    isOrphanSelected,
    pendingSelection: pendingSelectionState.items,
    validatedSelectionItems: validatedSelectionState.items,
    isAllAccountsSelected,
    isChildSelectorDisabled,
    pendingSelectionLimitReached,
    hasPendingSelection,
    selectionSummary: selectionView.summary as SelectionSummary,
  })

  return {
    selectionView,
    dropdownBehavior,
    props: {
      childrenCompanies,
      displayedPendingValue: selectionView.displayedPendingValue,
      onSelectionChange,
      onInputChange,
      inputValue: childrenSearchValue,
      isChildrenLoading,
      isChildSelectorDisabled,
      selectionLabel: selectionView.label,
      selectionDisplayValue: selectionView.displayValue,
      selectionPlaceholder: selectionView.placeholder,
      selectionSummary: selectionView.summary,
      limitTooltip: selectionView.limitTooltip,
      shouldShowSelectionTooltip: dropdownBehavior.shouldShowSelectionTooltip,
      shouldShowLimitTooltip: dropdownBehavior.shouldShowLimitTooltip,
      isChildDropdownOpen: dropdownBehavior.isChildDropdownOpen,
      openDropdown: dropdownBehavior.openDropdown,
      closeDropdown: dropdownBehavior.closeDropdown,
      getOptionDisabled,
      pendingAllAccountsSelection: pendingSelectionState.mode === 'allAccounts',
      canSelectAllChildren,
      onHoverChildInput: dropdownBehavior.onHoverChildInput,
      inputRef: dropdownBehavior.childInputRef,
    },
  }
}

export { useChildSelectorControl }
